import { useState, useRef, useEffect } from 'react';
import { Check, Package, Truck } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';

/**
 * VERIFICARE v14 - FIX PENTRU RELOAD
 * 
 * useEffect rulează DOAR când se DESCHIDE modalul (open: false -> true)
 * NU se resetează când initialItems se schimbă din cauza loadData!
 */

const STORAGE = 'toth_v14';

const formatPrice = (v) => v ? v.toFixed(2).replace('.', ',') : '0,00';

const beepOK = () => {
    try {
        const c = new AudioContext();
        const o = c.createOscillator();
        o.connect(c.destination);
        o.frequency.value = 800;
        o.start();
        o.stop(c.currentTime + 0.1);
    } catch(e){}
};

const beepERR = () => {
    try {
        const c = new AudioContext();
        const o = c.createOscillator();
        o.connect(c.destination);
        o.frequency.value = 200;
        o.start();
        o.stop(c.currentTime + 0.3);
    } catch(e){}
};

const speak = (p) => {
    try {
        if (!window.speechSynthesis) return;
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(String(Math.round(p * 100) / 100));
        u.lang = 'ro-RO';
        u.rate = 1.1;
        window.speechSynthesis.speak(u);
    } catch(e){}
};

// Storage
const load = (id) => {
    try {
        return (JSON.parse(localStorage.getItem(STORAGE) || '{}'))[id] || {};
    } catch { return {}; }
};

const save = (id, data) => {
    try {
        const d = JSON.parse(localStorage.getItem(STORAGE) || '{}');
        d[id] = data;
        localStorage.setItem(STORAGE, JSON.stringify(d));
    } catch(e){}
};

const clear = (id) => {
    try {
        const d = JSON.parse(localStorage.getItem(STORAGE) || '{}');
        delete d[id];
        localStorage.setItem(STORAGE, JSON.stringify(d));
    } catch(e){}
};

export const VerificationModalV12 = ({ open, onOpenChange, customer, initialItems, onFinalize }) => {
    const [items, setItems] = useState([]);
    const [input, setInput] = useState('');
    const inputRef = useRef(null);
    const lastScan = useRef({ code: '', time: 0 });
    const custId = useRef(null);
    const wasOpen = useRef(false); // Track dacă era deschis înainte

    // IMPORTANT: Încarcă items DOAR când modalul se DESCHIDE (false -> true)
    // NU când initialItems se schimbă!
    useEffect(() => {
        // Detectează tranziția false -> true
        if (open && !wasOpen.current && customer && initialItems) {
            custId.current = customer.customer_id;
            const saved = load(customer.customer_id);
            
            const newItems = initialItems.map(item => ({
                id: item.id,
                name: item.name,
                barcode: (item.barcode || '').toUpperCase().trim(),
                qty: item.quantity,
                price: item.unit_price ?? item.price,
                isFee: item.is_fee || item.source === 'taxa',
                verified: saved[item.id] || 0
            }));
            
            setItems(newItems);
            setInput('');
            setTimeout(() => inputRef.current?.focus(), 100);
        }
        
        // Când se închide, resetează
        if (!open && wasOpen.current) {
            // Nu reseta items aici - lasă-le pentru când se redeschide
        }
        
        wasOpen.current = open;
    }, [open]); // DOAR open în dependențe! NU customer, NU initialItems!

    // Salvează în localStorage
    const saveNow = (list) => {
        if (!custId.current) return;
        const map = {};
        list.forEach(i => { map[i.id] = i.verified; });
        save(custId.current, map);
    };

    // CLICK = toggle
    const handleClick = (id) => {
        setItems(prev => {
            const next = prev.map(i => {
                if (i.id !== id) return i;
                const v = i.verified >= i.qty ? 0 : i.verified + 1;
                if (v > i.verified && i.price) { beepOK(); speak(i.price); }
                return { ...i, verified: v };
            });
            saveNow(next);
            return next;
        });
        setTimeout(() => inputRef.current?.focus(), 50);
    };

    // TOATE
    const handleAll = () => {
        setItems(prev => {
            const next = prev.map(i => ({ ...i, verified: i.qty }));
            saveNow(next);
            return next;
        });
    };

    // SCANARE
    const handleScan = (code) => {
        const now = Date.now();
        const bc = (code || '').toUpperCase().trim();
        if (!bc) return;
        
        // Debounce 2 secunde
        if (bc === lastScan.current.code && now - lastScan.current.time < 2000) {
            return;
        }
        lastScan.current = { code: bc, time: now };
        
        setItems(prev => {
            const idx = prev.findIndex(i => !i.isFee && i.barcode === bc && i.verified < i.qty);
            
            if (idx >= 0) {
                const item = prev[idx];
                const v = item.verified + 1;
                
                beepOK();
                if (item.price) speak(item.price);
                
                if (v >= item.qty) {
                    toast.success(`✓ ${item.name} COMPLET!`, { duration: 2000 });
                } else {
                    toast.success(`${item.name}: ${v}/${item.qty}`, { duration: 1500 });
                }
                
                const next = [...prev];
                next[idx] = { ...item, verified: v };
                saveNow(next);
                return next;
            }
            
            const done = prev.find(i => !i.isFee && i.barcode === bc && i.verified >= i.qty);
            if (done) {
                toast.info(`${done.name} - deja verificat!`, { duration: 1500 });
                return prev;
            }
            
            beepERR();
            toast.error('Cod negăsit!', { duration: 2000 });
            return prev;
        });
    };

    const handleKey = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleScan(input);
            setInput('');
        }
    };

    // Stats
    const prods = items.filter(i => !i.isFee);
    const fee = items.find(i => i.isFee);
    const totalQty = prods.reduce((s, i) => s + i.qty, 0);
    const verifiedQty = prods.reduce((s, i) => s + i.verified, 0);
    const complete = prods.filter(i => i.verified >= i.qty).length;
    const hasUnverified = prods.some(i => i.verified < i.qty);

    const handleFinalize = () => {
        if (onFinalize) {
            const data = items.map(i => ({ id: i.id, verifiedCount: i.verified, quantity: i.qty }));
            onFinalize(data, {
                verifiedQty, totalQty, fullyVerifiedCount: complete, totalProducts: prods.length,
                unverifiedQty: totalQty - verifiedQty, hasUnverified,
                includeTransport: fee ? fee.verified >= fee.qty : false
            });
        }
        if (custId.current) clear(custId.current);
    };

    if (!customer) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        <Check className="inline mr-2 text-emerald-600" size={20} />
                        Verificare #{customer.customer_id}
                    </DialogTitle>
                    <DialogDescription className="sr-only">Verifică produse</DialogDescription>
                </DialogHeader>
                
                <div className="mt-4 space-y-4">
                    <div className="flex gap-2">
                        <input
                            ref={inputRef}
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKey}
                            placeholder="Scanează codul..."
                            className="flex-1 px-4 py-3 border rounded-lg text-lg"
                            autoComplete="off"
                            autoFocus
                        />
                        <Button onClick={handleAll} variant="outline" className="px-4">Toate</Button>
                    </div>

                    <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                        {items.map(item => {
                            const ok = item.verified >= item.qty;
                            const partial = item.verified > 0 && !ok;
                            
                            return (
                                <div
                                    key={item.id}
                                    onClick={() => handleClick(item.id)}
                                    className={`p-3 rounded-lg border-2 cursor-pointer ${
                                        item.isFee
                                            ? ok ? 'border-blue-500 bg-blue-50' : 'border-zinc-200 bg-zinc-50'
                                            : ok ? 'border-emerald-500 bg-emerald-50'
                                                : partial ? 'border-amber-400 bg-amber-50' : 'border-zinc-200'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                                item.isFee ? (ok ? 'bg-blue-500' : 'bg-zinc-300')
                                                    : ok ? 'bg-emerald-500' : partial ? 'bg-amber-400' : 'bg-zinc-300'
                                            }`}>
                                                {ok ? <Check className="text-white" size={18} />
                                                    : item.isFee ? <Truck className="text-white" size={16} />
                                                    : <Package className="text-white" size={16} />}
                                            </div>
                                            <div>
                                                <p className={`font-medium ${ok ? 'text-emerald-700' : ''}`}>
                                                    {item.isFee ? 'Taxa Transport' : item.name}
                                                </p>
                                                <p className="text-sm text-zinc-500">
                                                    {item.qty} × {formatPrice(item.price)} RON
                                                </p>
                                            </div>
                                        </div>
                                        <p className={`text-xl font-bold ${ok ? 'text-emerald-600' : partial ? 'text-amber-600' : 'text-zinc-400'}`}>
                                            {item.verified}/{item.qty}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="flex justify-between p-3 bg-zinc-100 rounded-lg">
                        <span>Verificate: <b className={verifiedQty === totalQty ? 'text-emerald-600' : 'text-amber-600'}>{verifiedQty}/{totalQty}</b></span>
                        <span>Complete: <b className={complete === prods.length ? 'text-emerald-600' : 'text-amber-600'}>{complete}/{prods.length}</b></span>
                    </div>

                    <div className="flex gap-3">
                        <Button onClick={() => onOpenChange(false)} variant="outline" className="flex-1">Anulează</Button>
                        <Button onClick={handleFinalize} className={`flex-1 ${hasUnverified ? 'bg-amber-500' : 'bg-emerald-500'}`}>
                            <Check size={18} className="mr-2" />Finalizează
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default VerificationModalV12;
